import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Spin, Modal, Col, Row, Card } from "antd";
import CorridorService from "../../../../../../services/CorridorService";
import { CorridorShow } from "../../../../../../models/Corridor";
import { SupervisionDetails, supervisionDetailsPosition } from "../../../../../../models/Supervision";
import ImageDisplayer from "../../../../../utils/ImageDisplayer";
import SweepPictureService from "../../../../../../services/PictureService";
import SweepThumbnailService from "../../../../../../services/ThumbnailService";
import UserContext from "../../../../../../context/UserContext";
import { useLanguage } from "../../../../../../context/LanguageContext";

const MAP_CELL_SIZE = 20;

// Modal component to display images
const ImageModal: React.FC<ImageModalProps> = ({ visible, sweepIdx, modalPosition, onClose }) => {
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
      title={`Position: ${modalPosition}`}
    >
      <Row justify="space-around" align="middle">
        <Col span="100px">
          <Card style={{ width: "230px", textAlign: "center", height: "100" }}>
            <ImageDisplayer
              fetchFunction={() => {
                const paddedPositionLabel = modalPosition.padStart(5, ' ');
                return SweepPictureService.getSweepPositionPicture(parseInt(sweepIdx), paddedPositionLabel);
              }}
              lowfetchFuntion={() => {
                const paddedPositionLabel = modalPosition.padStart(5, ' ');
                return SweepThumbnailService.getSweepPositionThumbnail(parseInt(sweepIdx), paddedPositionLabel);
              }}
              height={"100px"}
            />
          </Card>
        </Col>
        <Col span="100px">
          <Card style={{ width: "230px", textAlign: "center", height: "100" }}>

            <ImageDisplayer
              fetchFunction={() => {
                var paddedPositionLabel = modalPosition.padStart(5, ' ');
                return SweepPictureService.getSweepPalletPicture(parseInt(sweepIdx), paddedPositionLabel);
              }}
              lowfetchFuntion={() => {
                const paddedPositionLabel = modalPosition.padStart(5, ' ');
                return SweepThumbnailService.getSweepPalletThumbnail(parseInt(sweepIdx), paddedPositionLabel);
              }}
              height={"100px"}
            />
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};
interface ImageModalProps {
  visible: boolean;
  sweepIdx: string;
  modalPosition: string;
  onClose: () => void;
}

interface GridProps {
  corridorIdx: string | undefined;
  supervisiondetails: SupervisionDetails;
  sweepIdx: string;
}
type ButtonSize = 'small' | 'middle' | 'large';
const CorridorHistoryMap: React.FC<GridProps> = ({ corridorIdx, sweepIdx, supervisiondetails }) => {
  const { language } = useLanguage();
  const isMobile = useContext(UserContext).isMobile;
  const [loading, setLoading] = useState<boolean>(true);
  const [corridor, setCorridor] = useState<CorridorShow>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState<string>("");
  const positionsLookup = useMemo(() => {
    const lookup: Record<string, supervisionDetailsPosition> = {};
    for (const position of supervisiondetails.positions) {
      lookup[position.position_label] = position;
    }
    return lookup;
  }, [supervisiondetails.positions]);

  const buttonSize: ButtonSize = isMobile ? 'large' : 'small';
  const cellGap = buttonSize === 'large' ? 3 : 2;

  const updateContent = useCallback(() => {
    if (corridorIdx) {
      CorridorService.getOne(corridorIdx.toString()).then((corridor) => {
        if (corridor === null) {
          return;
        }
        setCorridor(corridor);
        setLoading(false);
      });
    }
  }, [corridorIdx]);

  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 500000);
    return () => clearInterval(interval);
  }, [updateContent]);

  const handlePopoverClick = useCallback((supervisionPosition: supervisionDetailsPosition | undefined) => {
    if (supervisionPosition) {
      setModalPosition(supervisionPosition.position_label)
      setModalVisible(true);
    }
  }, []);

  const columnCount = useMemo(() => {
    if (!corridor?.rack_template?.column_range) {
      return 0;
    }
    return corridor.rack_template.column_range[1] - corridor.rack_template.column_range[0] + 1;
  }, [corridor]);

  const levelIndexes = useMemo(() => {
    if (!corridor?.rack_template?.level_range) {
      return [] as number[];
    }
    const levels = corridor.rack_template.level_range[1] - corridor.rack_template.level_range[0] + 1;
    return Array.from({ length: levels }, (_, i) => i).reverse();
  }, [corridor]);

  return (
    <Spin spinning={loading}>
      <Row
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        {corridor && corridor.rack_template && (
          levelIndexes.map((levelIndex) => (
            <div
              key={`row-${levelIndex}`}
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${columnCount}, ${MAP_CELL_SIZE}px)`,
                gap: `${cellGap}px`,
                width: "max-content",
              }}
            >
              {corridor?.rack_template && Array.from({ length: corridor.rack_template.column_range[1] - corridor.rack_template.column_range[0] + 1 }, (_, j) => {
                let colText = corridor.rack_template?.column_range ? String(j + corridor.rack_template.column_range[0]).padStart(2, "0") : '';
                if (corridor.rack_template?.orientation === "right") {
                  colText = corridor.rack_template?.column_range ? String(corridor.rack_template.column_range[1] - j).padStart(2, "0") : '';
                }
                const levelText = corridor.rack_template?.level_range ? String(levelIndex + corridor.rack_template.level_range[0]) : '';
                const positionText = `${corridor.name}${colText}${levelText}`;
                // const matchingPosition = supervisiondetails.positions.find((position) => position.position_label === positionText);
                const matchingPosition = positionsLookup[positionText];
                let palletLabel = matchingPosition?.pallet_label ?? "Empty Position";
                if(language==="es"&&palletLabel==="Empty Position"){
                  palletLabel="Vacía"
                }
                palletLabel = matchingPosition?.state === "unknown" ?language==="es"?"Sin Explorar":"Unexplored" : palletLabel;
                const popoverContent = matchingPosition ? (language === "es" ? `Etiqueta de Pallet: ${palletLabel}` : `Pallet Label: ${palletLabel}`) : "";
                const buttonStyle = {
                  backgroundColor: "#FF5252",
                  width: `${MAP_CELL_SIZE}px`,
                  height: `${MAP_CELL_SIZE}px`,
                  border: "1px solid #1677ff",
                  borderRadius: "4px",
                  cursor: matchingPosition ? "pointer" : "default",
                  padding: 0,
                };
                if (matchingPosition) {
                  switch (matchingPosition.state) {
                    case "full":
                      buttonStyle.backgroundColor = "#4CAF50";
                      break;
                    case "empty":
                      buttonStyle.backgroundColor = "#a0d911";
                      break;
                    case "unknown":
                      buttonStyle.backgroundColor = "#FF5252";
                      break;
                    case "invalid":
                      buttonStyle.backgroundColor = "#FFC107";
                      break;
                    default:
                      buttonStyle.backgroundColor = "#bfbfbf";
                      break;
                  }
                }
                else {
                  buttonStyle.backgroundColor = "#bfbfbf";
                }
                return (
                  <button
                    type="button"
                    key={`button-${levelIndex}-${j}`}
                    style={buttonStyle}
                    title={!isMobile ? `${language === "es" ? "Posición" : "Position"}: ${positionText}\n${popoverContent}` : ""}
                    onClick={() => handlePopoverClick(matchingPosition)}
                  ></button>
                );
              })}
            </div>
          ))
        )}
        <ImageModal
          visible={modalVisible}
          modalPosition={modalPosition}
          sweepIdx={sweepIdx}
          onClose={() => setModalVisible(false)}
        />
      </Row>
    </Spin>
  );
};

export default CorridorHistoryMap;
